<template>
  <v-container>
    <div class="pa-3 display-1">
      統計情報ダッシュボード（β）
    </div>

    <v-row align="center" justify="center" dense>
      <v-col cols="12" xl="12" dense>
        <v-card v-if= "!isSystemAdmin" flat outlined class="ma-5 pa-3">
          <div class="subtitle-2 mb-2">
            <v-icon class="mr-1" color="primary" small>mdi-information-outline</v-icon>
            <span class="primary--text">メンテナンス機能について</span>
          </div>
          <p class="caption">
              この機能はシステム管理者のみ利用できる機能です。<br>
          </p>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- 日付範囲選択コントロール -->
    <v-row v-if="isSystemAdmin" class="mb-4">
      <v-col cols="12">
        <v-card class="pa-3">
          <div class="d-flex align-center">
            <v-select
              v-model="dateRange"
              :items="dateRangeOptions"
              label="表示期間"
              dense
              style="max-width: 200px;"
              @change="refreshData"
            ></v-select>
            <v-spacer></v-spacer>
            <div class="mr-4">
              <span class="caption">アクティブユーザー数:</span>
              <span class="font-weight-bold">{{ activeUserCount }}人</span>
            </div>
            <v-btn 
              color="primary" 
              small 
              @click="refreshData"
              :loading="loading"
            >
              更新
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- マイ機器登録グラフセクション -->
    <v-row v-if="isSystemAdmin">
      <v-col cols="12">
        <v-card class="mb-4">
          <v-card-title>
            マイ機器登録 日別推移
            <span class="caption ml-2" style="font-size: 10px; color: #666;">
              過去{{ dateRange }}日間
              <span v-if="dailyLabels.length > 0">
                （{{ dailyLabels[0] }} ～ {{ dailyLabels[dailyLabels.length - 1] }}）
              </span>
            </span>
            <v-spacer></v-spacer>
            <div class="caption" style="font-size: 10px;">
              最大: {{ Math.max(...dailyCounts) }}件 | 合計: {{ dailyCounts.reduce((a, b) => a + b, 0) }}件
            </div>
          </v-card-title>
          <v-card-text>
            <div @click="showChartDetails" style="cursor: pointer;">
              <v-sparkline
                :value="dailyCounts"
                color="blue"
                line-width="2"
                padding="16"
                smooth="10"
                auto-draw
                height="120"
              ></v-sparkline>
            </div>
            <div class="d-flex justify-space-between px-4 mt-1" style="font-size: 10px; color: #666;">
              <span v-for="(label, index) in dateAxisLabels" :key="index">
                {{ label }}
              </span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 分析依頼登録グラフセクション -->
    <v-row v-if="isSystemAdmin">
      <v-col cols="12">
        <v-card class="mb-4">
          <v-card-title>
            分析依頼登録 日別推移（Type別）
            <span class="caption ml-2" style="font-size: 10px; color: #666;">
              過去{{ dateRange }}日間
              <span v-if="dailyLabels.length > 0">
                （{{ dailyLabels[0] }} ～ {{ dailyLabels[dailyLabels.length - 1] }}）
              </span>
            </span>
            <v-spacer></v-spacer>
            <div class="d-flex align-center">
              <div class="caption mr-4" style="font-size: 10px;">
                Application最大: {{ Math.max(...applicationTypeData) }}件 | 合計: {{ applicationTypeData.reduce((a, b) => a + b, 0) }}件<br>
                Quote最大: {{ Math.max(...quoteTypeData) }}件 | 合計: {{ quoteTypeData.reduce((a, b) => a + b, 0) }}件
              </div>
              <div class="mr-4">
                <v-chip small :color="typeFilter === 'all' ? 'green' : 'grey'" text-color="white" class="mr-2" @click="typeFilter = 'all'" style="cursor: pointer;">すべて</v-chip>
                <v-chip small :color="typeFilter === 'application' || typeFilter === 'all' ? 'blue' : 'grey'" text-color="white" class="mr-2" @click="toggleTypeFilter('application')" style="cursor: pointer;">Application</v-chip>
                <v-chip small :color="typeFilter === 'quote' || typeFilter === 'all' ? 'orange' : 'grey'" text-color="white" @click="toggleTypeFilter('quote')" style="cursor: pointer;">Quote</v-chip>
              </div>
            </div>
          </v-card-title>
          <v-card-text>
            <div @click="showApplicationChartDetails" style="cursor: pointer; position: relative; min-height: 120px;">
              <!-- Application線 -->
              <v-sparkline
                v-if="typeFilter === 'all' || typeFilter === 'application'"
                :value="applicationTypeData"
                color="blue"
                line-width="2"
                padding="16"
                smooth="10"
                auto-draw
                height="120"
                :style="typeFilter === 'all' ? 'position: absolute; top: 0; left: 0; width: 100%;' : ''"
              ></v-sparkline>
              <!-- Quote線 -->
              <v-sparkline
                v-if="typeFilter === 'all' || typeFilter === 'quote'"
                :value="quoteTypeData"
                color="orange"
                line-width="2"
                padding="16"
                smooth="10"
                auto-draw
                height="120"
                :fill="false"
              ></v-sparkline>
            </div>
            <div class="d-flex justify-space-between px-4 mt-1" style="font-size: 10px; color: #666;">
              <span v-for="(label, index) in dateAxisLabels" :key="index">
                {{ label }}
              </span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 分析依頼登録グラフセクション（区分別） -->
    <v-row v-if="isSystemAdmin">
      <v-col cols="12">
        <v-card class="mb-4">
          <v-card-title>
            分析依頼登録 日別推移（区分別）
            <span class="caption ml-2" style="font-size: 10px; color: #666;">
              過去{{ dateRange }}日間
              <span v-if="dailyLabels.length > 0">
                （{{ dailyLabels[0] }} ～ {{ dailyLabels[dailyLabels.length - 1] }}）
              </span>
            </span>
            <v-spacer></v-spacer>
            <div class="d-flex align-center">
              <div class="caption mr-4" style="font-size: 10px;">
                一般最大: {{ Math.max(...generalCategoryData) }}件 | 合計: {{ generalCategoryData.reduce((a, b) => a + b, 0) }}件<br>
                航空機最大: {{ Math.max(...aircraftCategoryData) }}件 | 合計: {{ aircraftCategoryData.reduce((a, b) => a + b, 0) }}件
              </div>
              <div class="mr-4">
                <v-chip small :color="categoryFilter === 'all' ? 'green' : 'grey'" text-color="white" class="mr-2" @click="categoryFilter = 'all'" style="cursor: pointer;">すべて</v-chip>
                <v-chip small :color="categoryFilter === 'general' || categoryFilter === 'all' ? 'purple' : 'grey'" text-color="white" class="mr-2" @click="toggleCategoryFilter('general')" style="cursor: pointer;">一般</v-chip>
                <v-chip small :color="categoryFilter === 'aircraft' || categoryFilter === 'all' ? 'teal' : 'grey'" text-color="white" @click="toggleCategoryFilter('aircraft')" style="cursor: pointer;">航空機</v-chip>
              </div>
            </div>
          </v-card-title>
          <v-card-text>
            <div @click="showCategoryChartDetails" style="cursor: pointer; position: relative; min-height: 120px;">
              <!-- 一般線 -->
              <v-sparkline
                v-if="categoryFilter === 'all' || categoryFilter === 'general'"
                :value="generalCategoryData"
                color="purple"
                line-width="2"
                padding="16"
                smooth="10"
                auto-draw
                height="120"
                :style="categoryFilter === 'all' ? 'position: absolute; top: 0; left: 0; width: 100%;' : ''"
              ></v-sparkline>
              <!-- 航空機線 -->
              <v-sparkline
                v-if="categoryFilter === 'all' || categoryFilter === 'aircraft'"
                :value="aircraftCategoryData"
                color="teal"
                line-width="2"
                padding="16"
                smooth="10"
                auto-draw
                height="120"
                :fill="false"
              ></v-sparkline>
            </div>
            <div class="d-flex justify-space-between px-4 mt-1" style="font-size: 10px; color: #666;">
              <span v-for="(label, index) in dateAxisLabels" :key="index">
                {{ label }}
              </span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 納品明細書・報告書登録グラフセクション -->
    <v-row v-if="isSystemAdmin">
      <v-col cols="12">
        <v-card class="mb-4">
          <v-card-title>
            納品明細書・報告書登録 日別推移
            <span class="caption ml-2" style="font-size: 10px; color: #666;">
              過去{{ dateRange }}日間
              <span v-if="dailyLabels.length > 0">
                （{{ dailyLabels[0] }} ～ {{ dailyLabels[dailyLabels.length - 1] }}）
              </span>
            </span>
            <v-spacer></v-spacer>
            <div class="caption" style="font-size: 10px;">
              最大: {{ Math.max(...deliverablesDailyCounts) }}件 | 合計: {{ deliverablesDailyCounts.reduce((a, b) => a + b, 0) }}件
            </div>
          </v-card-title>
          <v-card-text>
            <div @click="showDeliverablesChartDetails" style="cursor: pointer;">
              <v-sparkline
                :value="deliverablesDailyCounts"
                color="orange"
                line-width="2"
                padding="16"
                smooth="10"
                auto-draw
                height="120"
              ></v-sparkline>
            </div>
            <!-- 横軸（X軸） -->
            <div class="d-flex justify-space-between px-4 mt-1" style="font-size: 10px; color: #666;">
              <span v-for="(label, index) in dateAxisLabels" :key="index">
                {{ label }}
              </span>
            </div>
            <div class="text-left mt-2">
              <div class="caption">
                過去{{ dateRange }}日間
                <span v-if="dailyLabels.length > 0">
                  （{{ dailyLabels[0] }} ～ {{ dailyLabels[dailyLabels.length - 1] }}）
                </span>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 依頼サンプルグラフセクション（OilTypeCategoryText別） -->
    <v-row v-if="isSystemAdmin">
      <v-col cols="12">
        <v-card class="mb-4">
          <v-card-title>
            依頼サンプル 日別推移（油種別）
            <span class="caption ml-2" style="font-size: 10px; color: #666;">
              過去{{ dateRange }}日間
              <span v-if="dailyLabels.length > 0">
                （{{ dailyLabels[0] }} ～ {{ dailyLabels[dailyLabels.length - 1] }}）
              </span>
            </span>
            <v-spacer></v-spacer>
            <div class="d-flex align-center">
              <div class="caption mr-4" style="font-size: 10px;">
                潤滑油: {{ sampleOilTypeData['潤滑油'].reduce((a, b) => a + b, 0) }}件 |
                燃料: {{ sampleOilTypeData['燃料'].reduce((a, b) => a + b, 0) }}件 |
                グリス: {{ sampleOilTypeData['グリス'].reduce((a, b) => a + b, 0) }}件<br>
                水溶性液体: {{ sampleOilTypeData['水溶性液体'].reduce((a, b) => a + b, 0) }}件 |
                水グリコール: {{ sampleOilTypeData['水グリコール'].reduce((a, b) => a + b, 0) }}件
              </div>
              <div class="mr-4">
                <v-chip small :color="sampleFilter === 'all' ? 'green' : 'grey'" text-color="white" class="mr-1" @click="sampleFilter = 'all'" style="cursor: pointer; font-size: 10px;">すべて</v-chip>
                <v-chip small :color="sampleFilter === '潤滑油' || sampleFilter === 'all' ? 'red' : 'grey'" text-color="white" class="mr-1" @click="toggleSampleFilter('潤滑油')" style="cursor: pointer; font-size: 10px;">潤滑油</v-chip>
                <v-chip small :color="sampleFilter === '燃料' || sampleFilter === 'all' ? 'blue' : 'grey'" text-color="white" class="mr-1" @click="toggleSampleFilter('燃料')" style="cursor: pointer; font-size: 10px;">燃料</v-chip>
                <v-chip small :color="sampleFilter === 'グリス' || sampleFilter === 'all' ? 'orange' : 'grey'" text-color="white" class="mr-1" @click="toggleSampleFilter('グリス')" style="cursor: pointer; font-size: 10px;">グリス</v-chip>
                <v-chip small :color="sampleFilter === '水溶性液体' || sampleFilter === 'all' ? 'cyan' : 'grey'" text-color="white" class="mr-1" @click="toggleSampleFilter('水溶性液体')" style="cursor: pointer; font-size: 10px;">水溶性液体</v-chip>
                <v-chip small :color="sampleFilter === '水グリコール' || sampleFilter === 'all' ? 'purple' : 'grey'" text-color="white" @click="toggleSampleFilter('水グリコール')" style="cursor: pointer; font-size: 10px;">水グリコール</v-chip>
              </div>
            </div>
          </v-card-title>
          <v-card-text>
            <div @click="showSampleChartDetails" style="cursor: pointer; position: relative; min-height: 120px;">
              <!-- 高さ確保用のダミー（すべて選択時のみ） -->
              <v-sparkline
                v-if="sampleFilter === 'all'"
                :value="sampleOilTypeData['潤滑油']"
                color="transparent"
                line-width="0"
                padding="16"
                height="120"
                :fill="false"
              ></v-sparkline>
              <!-- 潤滑油線 -->
              <v-sparkline
                v-if="sampleFilter === 'all' || sampleFilter === '潤滑油'"
                :value="sampleOilTypeData['潤滑油']"
                color="red"
                line-width="2"
                padding="16"
                smooth="10"
                auto-draw
                height="120"
                :fill="false"
                :style="sampleFilter === 'all' ? 'position: absolute; top: 0; left: 0; width: 100%;' : ''"
              ></v-sparkline>
              <!-- 燃料線 -->
              <v-sparkline
                v-if="sampleFilter === 'all' || sampleFilter === '燃料'"
                :value="sampleOilTypeData['燃料']"
                color="blue"
                line-width="2"
                padding="16"
                smooth="10"
                auto-draw
                height="120"
                :fill="false"
                :style="sampleFilter === 'all' ? 'position: absolute; top: 0; left: 0; width: 100%;' : ''"
              ></v-sparkline>
              <!-- グリス線 -->
              <v-sparkline
                v-if="sampleFilter === 'all' || sampleFilter === 'グリス'"
                :value="sampleOilTypeData['グリス']"
                color="orange"
                line-width="2"
                padding="16"
                smooth="10"
                auto-draw
                height="120"
                :fill="false"
                :style="sampleFilter === 'all' ? 'position: absolute; top: 0; left: 0; width: 100%;' : ''"
              ></v-sparkline>
              <!-- 水溶性液体線 -->
              <v-sparkline
                v-if="sampleFilter === 'all' || sampleFilter === '水溶性液体'"
                :value="sampleOilTypeData['水溶性液体']"
                color="cyan"
                line-width="2"
                padding="16"
                smooth="10"
                auto-draw
                height="120"
                :fill="false"
                :style="sampleFilter === 'all' ? 'position: absolute; top: 0; left: 0; width: 100%;' : ''"
              ></v-sparkline>
              <!-- 水グリコール線 -->
              <v-sparkline
                v-if="sampleFilter === 'all' || sampleFilter === '水グリコール'"
                :value="sampleOilTypeData['水グリコール']"
                color="purple"
                line-width="2"
                padding="16"
                smooth="10"
                auto-draw
                height="120"
                :fill="false"
                :style="sampleFilter === 'all' ? 'position: absolute; top: 0; left: 0; width: 100%;' : ''"
              ></v-sparkline>
            </div>
            <div class="d-flex justify-space-between px-4 mt-1" style="font-size: 10px; color: #666;">
              <span v-for="(label, index) in dateAxisLabels" :key="index">
                {{ label }}
              </span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- グラフ詳細ダイアログ -->
    <v-dialog v-model="chartDetailsDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">マイ機器登録 日別詳細</span>
        </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="[
              { text: '日付', value: 'date' },
              { text: '登録件数', value: 'count', align: 'end' }
            ]"
            :items="chartDetailsData"
            :items-per-page="10"
            class="elevation-1"
            dense
          >
            <template v-slot:[`item.count`]="{ item }">
              <span :class="item.count > 0 ? 'font-weight-bold primary--text' : ''">
                {{ item.count }}
              </span>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="chartDetailsDialog = false">
            閉じる
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Applicationグラフ詳細ダイアログ -->
    <v-dialog v-model="applicationChartDetailsDialog" max-width="800px">
      <v-card>
        <v-card-title>
          <span class="headline">分析依頼登録 日別詳細（{{ getTypeDisplayName() }}）</span>
        </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="[
              { text: '日付', value: 'date' },
              { text: 'Application', value: 'applicationCount', align: 'end' },
              { text: 'Quote', value: 'quoteCount', align: 'end' },
              { text: '合計', value: 'totalCount', align: 'end' }
            ]"
            :items="applicationChartDetailsData"
            :items-per-page="15"
            class="elevation-1"
            dense
          >
            <template v-slot:[`item.applicationCount`]="{ item }">
              <span :class="item.applicationCount > 0 ? 'font-weight-bold blue--text' : ''">
                {{ item.applicationCount }}
              </span>
            </template>
            <template v-slot:[`item.quoteCount`]="{ item }">
              <span :class="item.quoteCount > 0 ? 'font-weight-bold orange--text' : ''">
                {{ item.quoteCount }}
              </span>
            </template>
            <template v-slot:[`item.totalCount`]="{ item }">
              <span :class="item.totalCount > 0 ? 'font-weight-bold green--text' : ''">
                {{ item.totalCount }}
              </span>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" text @click="applicationChartDetailsDialog = false">
            閉じる
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 区分別グラフ詳細ダイアログ -->
    <v-dialog v-model="categoryChartDetailsDialog" max-width="800px">
      <v-card>
        <v-card-title>
          <span class="headline">分析依頼登録 日別詳細（区分別）</span>
        </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="[
              { text: '日付', value: 'date' },
              { text: '一般', value: 'generalCount', align: 'end' },
              { text: '航空機', value: 'aircraftCount', align: 'end' },
              { text: '合計', value: 'totalCount', align: 'end' }
            ]"
            :items="categoryChartDetailsData"
            :items-per-page="15"
            class="elevation-1"
            dense
          >
            <template v-slot:[`item.generalCount`]="{ item }">
              <span :class="item.generalCount > 0 ? 'font-weight-bold purple--text' : ''">
                {{ item.generalCount }}
              </span>
            </template>
            <template v-slot:[`item.aircraftCount`]="{ item }">
              <span :class="item.aircraftCount > 0 ? 'font-weight-bold teal--text' : ''">
                {{ item.aircraftCount }}
              </span>
            </template>
            <template v-slot:[`item.totalCount`]="{ item }">
              <span :class="item.totalCount > 0 ? 'font-weight-bold green--text' : ''">
                {{ item.totalCount }}
              </span>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" text @click="categoryChartDetailsDialog = false">
            閉じる
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 納品明細書・報告書グラフ詳細ダイアログ -->
    <v-dialog v-model="deliverablesChartDetailsDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">納品明細書・報告書登録 日別詳細</span>
        </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="[
              { text: '日付', value: 'date' },
              { text: '登録件数', value: 'count', align: 'end' }
            ]"
            :items="deliverablesChartDetailsData"
            :items-per-page="10"
            class="elevation-1"
            dense
          >
            <template v-slot:[`item.count`]="{ item }">
              <span :class="item.count > 0 ? 'font-weight-bold orange--text' : ''">
                {{ item.count }}
              </span>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="orange darken-1" text @click="deliverablesChartDetailsDialog = false">
            閉じる
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 依頼サンプルグラフ詳細ダイアログ -->
    <v-dialog v-model="sampleChartDetailsDialog" max-width="900px">
      <v-card>
        <v-card-title>
          <span class="headline">依頼サンプル 日別詳細（油種別）</span>
        </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="[
              { text: '日付', value: 'date' },
              { text: '潤滑油', value: '潤滑油', align: 'end' },
              { text: '燃料', value: '燃料', align: 'end' },
              { text: 'グリス', value: 'グリス', align: 'end' },
              { text: '水溶性液体', value: '水溶性液体', align: 'end' },
              { text: '水グリコール', value: '水グリコール', align: 'end' },
              { text: '合計', value: 'totalCount', align: 'end' }
            ]"
            :items="sampleChartDetailsData"
            :items-per-page="15"
            class="elevation-1"
            dense
          >
            <template v-slot:[`item.潤滑油`]="{ item }">
              <span :class="item['潤滑油'] > 0 ? 'font-weight-bold red--text' : ''">
                {{ item['潤滑油'] }}
              </span>
            </template>
            <template v-slot:[`item.燃料`]="{ item }">
              <span :class="item['燃料'] > 0 ? 'font-weight-bold blue--text' : ''">
                {{ item['燃料'] }}
              </span>
            </template>
            <template v-slot:[`item.グリス`]="{ item }">
              <span :class="item['グリス'] > 0 ? 'font-weight-bold orange--text' : ''">
                {{ item['グリス'] }}
              </span>
            </template>
            <template v-slot:[`item.水溶性液体`]="{ item }">
              <span :class="item['水溶性液体'] > 0 ? 'font-weight-bold cyan--text' : ''">
                {{ item['水溶性液体'] }}
              </span>
            </template>
            <template v-slot:[`item.水グリコール`]="{ item }">
              <span :class="item['水グリコール'] > 0 ? 'font-weight-bold purple--text' : ''">
                {{ item['水グリコール'] }}
              </span>
            </template>
            <template v-slot:[`item.totalCount`]="{ item }">
              <span :class="item.totalCount > 0 ? 'font-weight-bold green--text' : ''">
                {{ item.totalCount }}
              </span>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" text @click="sampleChartDetailsDialog = false">
            閉じる
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    </v-container>
</template>

<script>

import { API, graphqlOperation} from "aws-amplify"
import { listMachines } from '../graphql/queries'
import { listApplications } from '../graphql/queries'
import { listDeliverabless } from '../graphql/queries'
import { listEmployees } from '../graphql/queries'
import { listSamples } from '../graphql/queries'
import utils  from '../utils/utils.js'

export default {

  name: "Maintenance01",
  mixins:[utils],
  head: {
    title: { inner: '統計情報ダッシュボード'},
  },
  data() {
    return {
      search: '',
      page: 1,
      pageCount: 0,
      totalVisible: 7,
      itemsPerPage: 5,
      itemsPerPageList: [ 5,10,30 ],
      loading: false,
      headers: [
        {
          text: 'id',
          align: 'start',
          value: 'id',
          class: "grey darken-2 white--text",
        },
        {
          text: 'Name',
          value: 'Name',
          class: "grey darken-2 white--text"

        },
        {
          text: 'CustomerMachineID',
          value: 'CustomerMachineID',
          class: "grey darken-2 white--text"

        },
        {
          text: 'OilTypeCategory',
          value: 'OilTypeCategory',
          class: "grey darken-2 white--text"

        },
        {
          text: 'OilTypeCategoryText',
          value: 'OilTypeCategoryText',
          class: "grey darken-2 white--text"
        },
        {
          text: 'OilType',
          value: 'OilType',
          class: "grey darken-2 white--text"
        },
        {
          text: 'OilTypeText',
          value: 'OilTypeText',
          class: "grey darken-2 white--text"
        },
        {
          text: 'AnalizeTypeGroup',
          value: 'AnalizeTypeGroup',
          class: "grey darken-2 white--text"
        },
        {
          text: 'AnalizeType',
          value: 'AnalizeType',
          class: "grey darken-2 white--text"
        },
        {
          text: 'isHazardousMaterials',
          value: 'isHazardousMaterials',
          class: "grey darken-2 white--text"
        },
        {
          text: 'isAccept',
          value: 'isAccept',
          class: "grey darken-2 white--text"
        },
        { text: 'Actions',
          value: 'actions',
          sortable: false,
          class: "grey darken-2 white--text"
        },
      ],
      machines: [],
      analizeTypes: [],
      machinedialog: false,
      editedMachineIndex: -1,
      editedMachine: {
        AnalizeType: '',
        OilType: '',
        OilTypeText: '',
        OilTypeCategory: '',
        OilTypeCategoryText: '',
        isHazardousMaterials: '',
        isAccept: '',
      },
      defaultMachine: {
        AnalizeType: '',
        OilType: '',
        OilTypeText: '',
        OilTypeCategory: '',
        OilTypeCategoryText: '',
        isHazardousMaterials: '',
        isAccept: '',
      },
      fee: 0,
      discount: 0,


      curTime: "",
      addTime: "",
      calcTimeResult: "",
      typeUpdateLoading: false,
      typeUpdateResult: "",
      dailyCounts: [],
      dailyLabels: [],
      displayLabels: [],
      dateAxisLabels: [],
      chartData: {},
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true
            }
          }]
        }
      },
      chartDetailsDialog: false,
      chartDetailsData: [],
      applicationDailyCounts: [],
      applicationChartDetailsDialog: false,
      applicationChartDetailsData: [],
      deliverablesDailyCounts: [],
      deliverablesChartDetailsDialog: false,
      deliverablesChartDetailsData: [],
      selectedApplicationType: 'all',
      applicationTypeData: [],
      quoteTypeData: [],
      generalCategoryData: [],
      aircraftCategoryData: [],
      categoryChartDetailsDialog: false,
      categoryChartDetailsData: [],
      typeFilter: 'all',
      categoryFilter: 'all',
      activeUserCount: 0,
      sampleOilTypeData: {
        '潤滑油': [],
        '燃料': [],
        'グリス': [],
        '水溶性液体': [],
        '水グリコール': []
      },
      sampleChartDetailsDialog: false,
      sampleChartDetailsData: [],
      sampleFilter: 'all',
      dateRange: 30,
      dateRangeOptions: [
        { text: '過去7日間', value: 7 },
        { text: '過去30日間', value: 30 },
        { text: '過去90日間', value: 90 },
        { text: '過去1年間', value: 365 }
      ]
    };
  },
  filters: {
  },
  computed: {
    formTitle () {
      return this.editedIndex === -1 ? 'New Item' : 'Edit Item'
    },
    isAdmin () {
      return this.$store.state.isAdmin
    },
    isSystemAdmin : function() {
      return this.$store.state.isSystemAdmin
    }
  },
  mounted: async function () {
/*
    let analizeTypeList = await API.graphql(graphqlOperation(listAnalizeTypes))
    this.analizeTypes = analizeTypeList.data.listAnalizeTypes.items

    let machineList = await API.graphql(graphqlOperation(listMachines))
    this.machines = machineList.data.listMachines.items
*/
    if (this.isSystemAdmin) {
      this.loadDailyData()
      this.loadApplicationDailyData()
      this.loadCategoryDailyData()
      this.loadDeliverablesDailyData()
      this.loadSampleDailyData()
      this.loadActiveUserCount()
    }
  },
  methods: {
    async getAllItems(queryOperation, variables = {}) {
      let allItems = []
      let nextToken = null
      
      do {
        const result = await API.graphql(graphqlOperation(queryOperation, {
          ...variables,
          limit: 1000,
          nextToken
        }))
        
        const items = result.data[Object.keys(result.data)[0]].items
        allItems = allItems.concat(items)
        nextToken = result.data[Object.keys(result.data)[0]].nextToken
      } while (nextToken)
      
      return allItems
    },

    showChartDetails() {
      console.log('Chart clicked!')
      this.chartDetailsData = this.dailyLabels.map((label, index) => ({
        date: label,
        count: this.dailyCounts[index]
      })).filter(item => item.count > 0)
      this.chartDetailsDialog = true
    },

    showApplicationChartDetails() {
      console.log('Application chart clicked!')
      this.applicationChartDetailsData = this.dailyLabels.map((label, index) => ({
        date: label,
        applicationCount: this.applicationTypeData[index] || 0,
        quoteCount: this.quoteTypeData[index] || 0,
        totalCount: (this.applicationTypeData[index] || 0) + (this.quoteTypeData[index] || 0)
      })).filter(item => item.totalCount > 0)
      this.applicationChartDetailsDialog = true
    },

    getApplicationCountsByType() {
      switch (this.selectedApplicationType) {
        case 'Application':
          return this.applicationTypeData
        case 'Deliverables':
          return this.deliverablesTypeData
        default:
          return this.applicationDailyCounts
      }
    },

    getApplicationChartColor() {
      switch (this.selectedApplicationType) {
        case 'Application':
          return 'blue'
        case 'Deliverables':
          return 'orange'
        default:
          return 'green'
      }
    },

    getTypeDisplayName() {
      switch (this.selectedApplicationType) {
        case 'Application':
          return 'Application'
        case 'Deliverables':
          return 'Deliverables'
        default:
          return '全体'
      }
    },

    showCategoryChartDetails() {
      console.log('Category chart clicked!')
      this.categoryChartDetailsData = this.dailyLabels.map((label, index) => ({
        date: label,
        generalCount: this.generalCategoryData[index] || 0,
        aircraftCount: this.aircraftCategoryData[index] || 0,
        totalCount: (this.generalCategoryData[index] || 0) + (this.aircraftCategoryData[index] || 0)
      })).filter(item => item.totalCount > 0)
      this.categoryChartDetailsDialog = true
    },

    toggleTypeFilter(type) {
      this.typeFilter = this.typeFilter === type ? 'all' : type
    },

    toggleCategoryFilter(category) {
      this.categoryFilter = this.categoryFilter === category ? 'all' : category
    },

    getSampleFilterColor(oilType) {
      const colors = {
        '潤滑油': 'red',
        '燃料': 'blue',
        'グリス': 'orange',
        '水溶性液体': 'cyan',
        '水グリコール': 'purple'
      }
      return colors[oilType] || 'grey'
    },

    toggleSampleFilter(oilType) {
      this.sampleFilter = this.sampleFilter === oilType ? 'all' : oilType
    },

    showSampleChartDetails() {
      console.log('Sample chart clicked!')
      this.sampleChartDetailsData = this.dailyLabels.map((label, index) => {
        const data = { date: label }
        let totalCount = 0
        Object.keys(this.sampleOilTypeData).forEach(oilType => {
          data[oilType] = this.sampleOilTypeData[oilType][index] || 0
          totalCount += data[oilType]
        })
        data.totalCount = totalCount
        return data
      }).filter(item => item.totalCount > 0)
      this.sampleChartDetailsDialog = true
    },

    async loadSampleDailyData() {
      const oilTypes = ['潤滑油', '燃料', 'グリス', '水溶性液体', '水グリコール']
      const promises = oilTypes.map(oilType => this.getSampleOilTypeDailyData(oilType))
      const results = await Promise.all(promises)
      
      oilTypes.forEach((oilType, index) => {
        this.sampleOilTypeData[oilType] = results[index].map(d => d.count)
      })
    },

    async getSampleOilTypeDailyData(oilType) {
      try {
        const endDate = new Date()
        const startDate = new Date()
        startDate.setDate(startDate.getDate() - this.dateRange)

        const items = await this.getAllItems(listSamples, {
          filter: {
            createdAt: {
              between: [startDate.toISOString(), endDate.toISOString()]
            },
            Status: {
              eq: '10000000'
            },
            OilTypeCategoryText: {
              eq: oilType
            }
          }
        })

        const grouped = {}
        items.forEach(item => {
          const date = item.createdAt.split('T')[0]
          grouped[date] = (grouped[date] || 0) + 1
        })

        const dailyData = []
        for (let i = this.dateRange - 1; i >= 0; i--) {
          const date = new Date()
          date.setDate(date.getDate() - i)
          const dateStr = date.toISOString().split('T')[0]
          dailyData.push({
            date: dateStr,
            count: grouped[dateStr] || 0
          })
        }

        return dailyData
      } catch (error) {
        console.error(`${oilType}データ取得エラー:`, error)
        return []
      }
    },

    showDeliverablesChartDetails() {
      console.log('Deliverables chart clicked!')
      this.deliverablesChartDetailsData = this.dailyLabels.map((label, index) => ({
        date: label,
        count: this.deliverablesDailyCounts[index]
      })).filter(item => item.count > 0)
      this.deliverablesChartDetailsDialog = true
    },

    async refreshData() {
      this.loading = true
      await Promise.all([
        this.loadDailyData(),
        this.loadApplicationDailyData(),
        this.loadCategoryDailyData(),
        this.loadDeliverablesDailyData(),
        this.loadSampleDailyData(),
        this.loadActiveUserCount()
      ])
      this.loading = false
    },

    async loadActiveUserCount() {
      try {
        const items = await this.getAllItems(listEmployees)
        const activeUsers = items.filter(employee => 
          employee.UserPoolID && employee.UserPoolID !== '000000000'
        )
        this.activeUserCount = activeUsers.length
      } catch (error) {
        console.error('Active user count error:', error)
        this.activeUserCount = 0
      }
    },

    async loadApplicationDailyData() {
      const [applicationData, quoteData] = await Promise.all([
        this.getApplicationDailyRegistrationData(),
        this.getQuoteDailyRegistrationData()
      ])
      
      this.applicationTypeData = applicationData.map(d => d.count)
      this.quoteTypeData = quoteData.map(d => d.count)
      this.applicationDailyCounts = applicationData.map((d, index) => 
        d.count + (quoteData[index]?.count || 0)
      )
    },

    async loadCategoryDailyData() {
      const [generalData, aircraftData] = await Promise.all([
        this.getGeneralCategoryDailyData(),
        this.getAircraftCategoryDailyData()
      ])
      
      this.generalCategoryData = generalData.map(d => d.count)
      this.aircraftCategoryData = aircraftData.map(d => d.count)
    },

    async getGeneralCategoryDailyData() {
      try {
        const endDate = new Date()
        const startDate = new Date()
        startDate.setDate(startDate.getDate() - this.dateRange)

        const items = await this.getAllItems(listApplications, {
          filter: {
            createdAt: {
              between: [startDate.toISOString(), endDate.toISOString()]
            },
            Type: {
              eq: 'Application'
            },
            AnalizeTypeCategory: {
              eq: '一般'
            }
          }
        })

        const grouped = {}
        items.forEach(item => {
          const date = item.createdAt.split('T')[0]
          grouped[date] = (grouped[date] || 0) + 1
        })

        const dailyData = []
        for (let i = this.dateRange - 1; i >= 0; i--) {
          const date = new Date()
          date.setDate(date.getDate() - i)
          const dateStr = date.toISOString().split('T')[0]
          dailyData.push({
            date: dateStr,
            count: grouped[dateStr] || 0
          })
        }

        return dailyData
      } catch (error) {
        console.error('一般データ取得エラー:', error)
        return []
      }
    },

    async getAircraftCategoryDailyData() {
      try {
        const endDate = new Date()
        const startDate = new Date()
        startDate.setDate(startDate.getDate() - this.dateRange)

        const items = await this.getAllItems(listApplications, {
          filter: {
            createdAt: {
              between: [startDate.toISOString(), endDate.toISOString()]
            },
            Type: {
              eq: 'Application'
            },
            AnalizeTypeCategory: {
              eq: '航空機'
            }
          }
        })

        const grouped = {}
        items.forEach(item => {
          const date = item.createdAt.split('T')[0]
          grouped[date] = (grouped[date] || 0) + 1
        })

        const dailyData = []
        for (let i = this.dateRange - 1; i >= 0; i--) {
          const date = new Date()
          date.setDate(date.getDate() - i)
          const dateStr = date.toISOString().split('T')[0]
          dailyData.push({
            date: dateStr,
            count: grouped[dateStr] || 0
          })
        }

        return dailyData
      } catch (error) {
        console.error('航空機データ取得エラー:', error)
        return []
      }
    },

    async loadDeliverablesDailyData() {
      const dailyData = await this.getDeliverablesDailyRegistrationData()
      this.deliverablesDailyCounts = dailyData.map(d => d.count)
    },

    async getDeliverablesDailyRegistrationData() {
      try {
        const endDate = new Date()
        const startDate = new Date()
        startDate.setDate(startDate.getDate() - this.dateRange)

        const items = await this.getAllItems(listDeliverabless)

        const grouped = {}
        items.forEach(item => {
          const itemDate = new Date(item.createdAt)
          if (itemDate >= startDate && itemDate <= endDate) {
            const date = item.createdAt.split('T')[0]
            grouped[date] = (grouped[date] || 0) + 1
          }
        })

        const dailyData = []
        for (let i = this.dateRange - 1; i >= 0; i--) {
          const date = new Date()
          date.setDate(date.getDate() - i)
          const dateStr = date.toISOString().split('T')[0]
          dailyData.push({
            date: dateStr,
            count: grouped[dateStr] || 0
          })
        }

        return dailyData
      } catch (error) {
        console.error('Deliverables データ取得エラー:', error)
        return []
      }
    },

    async getApplicationDailyRegistrationData() {
      try {
        const endDate = new Date()
        const startDate = new Date()
        startDate.setDate(startDate.getDate() - this.dateRange)

        const items = await this.getAllItems(listApplications, {
          filter: {
            createdAt: {
              between: [startDate.toISOString(), endDate.toISOString()]
            },
            Type: {
              eq: 'Application'
            }
          }
        })

        const grouped = {}
        items.forEach(item => {
          const date = item.createdAt.split('T')[0]
          grouped[date] = (grouped[date] || 0) + 1
        })

        const dailyData = []
        for (let i = this.dateRange - 1; i >= 0; i--) {
          const date = new Date()
          date.setDate(date.getDate() - i)
          const dateStr = date.toISOString().split('T')[0]
          dailyData.push({
            date: dateStr,
            count: grouped[dateStr] || 0
          })
        }

        return dailyData
      } catch (error) {
        console.error('Application データ取得エラー:', error)
        return []
      }
    },

    async getQuoteDailyRegistrationData() {
      try {
        const endDate = new Date()
        const startDate = new Date()
        startDate.setDate(startDate.getDate() - this.dateRange)

        const items = await this.getAllItems(listApplications, {
          filter: {
            createdAt: {
              between: [startDate.toISOString(), endDate.toISOString()]
            },
            Type: {
              eq: 'Quote'
            }
          }
        })

        const grouped = {}
        items.forEach(item => {
          const date = item.createdAt.split('T')[0]
          grouped[date] = (grouped[date] || 0) + 1
        })

        const dailyData = []
        for (let i = this.dateRange - 1; i >= 0; i--) {
          const date = new Date()
          date.setDate(date.getDate() - i)
          const dateStr = date.toISOString().split('T')[0]
          dailyData.push({
            date: dateStr,
            count: grouped[dateStr] || 0
          })
        }

        return dailyData
      } catch (error) {
        console.error('Quote データ取得エラー:', error)
        return []
      }
    },

    async loadDailyData() {
      const dailyData = await this.getDailyRegistrationData()
      this.dailyCounts = dailyData.map(d => d.count)
      this.dailyLabels = dailyData.map(d => {
        const date = new Date(d.date)
        return `${date.getMonth() + 1}/${date.getDate()}`
      })
      console.log('Daily data loaded:', { dailyData, dailyCounts: this.dailyCounts, dailyLabels: this.dailyLabels })
      
      // 横軸用の日付ラベル（6個程度）
      this.dateAxisLabels = []
      const step = Math.max(1, Math.floor(this.dateRange / 6))
      for (let i = 0; i < this.dateRange; i += step) {
        if (this.dailyLabels[i]) {
          this.dateAxisLabels.push(this.dailyLabels[i])
        }
      }
    },

    async getDailyRegistrationData() {
      try {
        // 動的な日付範囲のデータを取得
        const endDate = new Date()
        const startDate = new Date()
        startDate.setDate(startDate.getDate() - this.dateRange)

        const items = await this.getAllItems(listMachines, {
          filter: {
            createdAt: {
              between: [startDate.toISOString(), endDate.toISOString()]
            }
          }
        })

        // 日付ごとにグループ化
        const grouped = {}
        items.forEach(item => {
          const date = item.createdAt.split('T')[0]
          grouped[date] = (grouped[date] || 0) + 1
        })

        // 指定期間分の配列を作成（0件の日も含む）
        const dailyData = []
        for (let i = this.dateRange - 1; i >= 0; i--) {
          const date = new Date()
          date.setDate(date.getDate() - i)
          const dateStr = date.toISOString().split('T')[0]
          dailyData.push({
            date: dateStr,
            count: grouped[dateStr] || 0
          })
        }

        return dailyData
      } catch (error) {
        console.error('データ取得エラー:', error)
        return []
      }
    },
  }
};
</script>
